import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLoader from '../../components/loader/PageLoader';
import useAxios from '../../axios/useAxios';
import { findUser } from '../../actions/userActions';
import { useDispatch, useSelector } from 'react-redux';
import PikkoPointHeader from '../../components/card/PikkoPointHeader';
import ScanFooter from './ScanFooter';
import NavigationMenu from '../../components/slider/NavigationMenu';
import {
  ACCOUNT,
  LOGIN,
  SET_ACCOUNT,
  UPDATE_USER,
} from '../../constants/routes';
import UserInfo from '../../components/UserInfo.jsx';
import SwipperTuto from '../../components/Swipper';
import step1 from '../../styles/img/tuto/step1.svg';
import step2 from '../../styles/img/tuto/step2.svg';
import step3 from '../../styles/img/tuto/step3.svg';
import { isLessThan_Minutes } from '../../utils/Validator';
import { isAppPWA } from '../../utils/helper-functions.js';
import { useUserLoginStatus } from '../../utils/hooks.js';
import { FooterAccount } from '../../components/footerAccount.jsx';
import { Streaks } from '../../components/card/Streaks.jsx';
import { Statistics } from '../../components/card/Statistics.jsx';
import { IoIosSettings } from 'react-icons/io';
import { useFunnelTracking } from '../../posthog/funnelTracking';
import { usePerformanceContext } from '../../context/PerformanceContext.jsx';

const AccountScreen = () => {
  const navigate = useNavigate();
  const axiosInstance = useAxios();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const [brands, setBrands] = useState([]);
  const [showNavigationMenu, setShowNavigationMenu] = useState(false);
  const tutoRef = useRef();
  const { markComplete } = usePerformanceContext();
  const [TutoCurrentIndex, setTutoCurrentIndex] = useState(0);
  const [view, setView] = useState(false);
  const [selectedItem, setSelectedItem] = useState(4);
  const previousUserId = useRef(null);

  const userId = useMemo(() => user?._id, [user?._id]);

  const isPWA = isAppPWA();
  const isLoggedIn = useUserLoginStatus();
  const { trackPageViewAccount } = useFunnelTracking();

  useEffect(() => {
    trackPageViewAccount({
      isLoggedIn: isLoggedIn,
    });
  }, [trackPageViewAccount, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate(LOGIN);
      return;
    }

    if (isPWA && !user.favoriteStore) {
      navigate(SET_ACCOUNT);
    }
  }, [isPWA, isLoggedIn, navigate, user.favoriteStore]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (userId && previousUserId.current !== userId) {
      previousUserId.current = userId;

      axiosInstance.get('/brands').then((response) => {
        setBrands(response.data);
      });

      dispatch(findUser(user, axiosInstance, navigate));
    }
  }, [userId, user, axiosInstance, dispatch, navigate]);

  useEffect(() => {
    if (user._id && !user.userLoading && brands.length > 0) {
      markComplete();
    }
  }, [user._id, user.userLoading, brands, markComplete]);

  useEffect(() => {
    const element = document.querySelector('.cercle_account_coin_border');
    if (element) {
      element.style.height = `${(user.fidelity / 70) * 100}%`;
    }
  }, [user]);

  const onClickHelp = () => {
    setView(!view);
    setTutoCurrentIndex(0);
  };
  if (!user?._id) {
    return <PageLoader />;
  }

  return (
    <>
      <div
        id="page_account"
        className={`w-full h-full overflow-y-scroll ${isPWA ? 'pwa' : ''}`}
      >
        <NavigationMenu
          isOpen={showNavigationMenu}
          onClose={() => setShowNavigationMenu(false)}
          user={user}
        />

        <div className="page_account_body pb-10">
          <div className="flex flex-col items-start justify-start w-full h-full">
            <UserInfo isNewUser={isLessThan_Minutes(user, 10)} />

            <div className="w-full h-full ">
              <button
                type="button"
                className="question-mark-account box-shadow-none"
                onClick={() => navigate(UPDATE_USER)}
              >
                <IoIosSettings size={30} />
              </button>
              <div className="page_account_content">
                <PikkoPointHeader
                  user={user}
                  notEuroVersion={true}
                  isMainPage
                  onClick={() => setShowNavigationMenu(true)}
                />
                <div className="page_account_second_content">
                  <div className="page_account_second_content_title">
                    Série
                  </div>
                  <Streaks user={user} />
                  <div className="page_account_second_content_title">
                    Records
                  </div>
                  <Statistics user={user} />
                </div>
              </div>
              <SwipperTuto
                ref={tutoRef}
                close={onClickHelp}
                TutoCurrentIndex={TutoCurrentIndex}
                setTutoCurrentIndex={setTutoCurrentIndex}
                view={view}
                slides={[
                  <div key="1" className="tuto_box">
                    <div className="tuto-container">
                      <img src={step1} alt="step1" />
                      <p className="tuto-bold">SCANNER</p>
                      <div className="tuto-text">
                        <span className="tuto-title">
                          Scanne les codes barres de tes articles pour les
                          ajouter au panier
                        </span>
                      </div>
                    </div>
                  </div>,
                  <div key="2" className="tuto_box">
                    <div className="tuto-container">
                      <img src={step2} alt="step2" />
                      <p className="tuto-bold">PAYER</p>
                      <div className="tuto-text">
                        <span className="tuto-title">
                          Paye tes courses sur ton smartphone sans faire la
                          queue
                        </span>
                      </div>
                    </div>
                  </div>,
                  <div key="3" className="tuto_box">
                    <div className="tuto-container">
                      <img src={step3} alt="step3" />
                      <p className="tuto-bold">VALIDER</p>
                      <div className="tuto-text">
                        <span className="tuto-title">
                          Dirige-toi vers la sortie express et scanne le QR
                          code de sortie
                        </span>
                      </div>
                    </div>
                  </div>,
                ]}
              />
            </div>
          </div>
        </div>
        <ScanFooter originPath={ACCOUNT} />
      </div>
      <FooterAccount
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
      />
    </>
  );
};

export default AccountScreen;
